import {
  AiEngineRoutingHistoryEntry,
  AiEngineRoutingSnapshot
} from "@domain/ai_engine"
import useTranslate from "@hooks/use_translate"
import HistoryIcon from "@mui/icons-material/History"
import {
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from "@mui/material"
import dayjs from "dayjs"

import { ReasonDialogRequest } from "./ReasonDialog"

type HistorySectionProps = {
  history: AiEngineRoutingHistoryEntry[]
  ask: (request: ReasonDialogRequest) => void
  rollback: (historyIndex: number, reason: string) => Promise<void>
}

const describeSnapshot = (snapshot: AiEngineRoutingSnapshot) =>
  snapshot.defaults
    .map((entry) => `${entry.capability}=${entry.version}`)
    .join(", ")

/**
 * Every change so far, newest first. "Restore" puts the routing back to exactly what it was
 * right BEFORE that entry was written — the snapshot each entry keeps.
 */
export default function HistorySection({
  history,
  ask,
  rollback
}: HistorySectionProps) {
  const { translate } = useTranslate()
  const rows = [...history].sort((a, b) => b.index - a.index)

  const restore = (entry: AiEngineRoutingHistoryEntry) =>
    ask({
      title: translate("ai_engine_rollback_title"),
      summary: translate("ai_engine_rollback_summary")
        .replace("{at}", dayjs(entry.at).format("DD.MM.YYYY HH:mm"))
        .replace("{defaults}", describeSnapshot(entry.snapshot)),
      danger: true,
      onConfirm: (reason) => rollback(entry.index, reason)
    })

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack gap={2}>
        <Stack>
          <Typography variant="subtitle1" fontWeight={600}>
            {translate("ai_engine_history_title")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {translate("ai_engine_history_desc")}
          </Typography>
        </Stack>
        {rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {translate("ai_engine_history_empty")}
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{translate("ai_engine_at")}</TableCell>
                <TableCell>{translate("ai_engine_by")}</TableCell>
                <TableCell>{translate("ai_engine_reason")}</TableCell>
                <TableCell>
                  {translate("ai_engine_history_state_before")}
                </TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((entry) => (
                <TableRow key={entry.index}>
                  <TableCell>
                    {dayjs(entry.at).format("DD.MM.YYYY HH:mm")}
                  </TableCell>
                  <TableCell>{entry.by}</TableCell>
                  <TableCell>{entry.reason}</TableCell>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                    {describeSnapshot(entry.snapshot)}
                    {entry.snapshot.pins.length > 0 &&
                      ` · ${translate("ai_engine_history_pins").replace(
                        "{count}",
                        String(entry.snapshot.pins.length)
                      )}`}
                    {entry.snapshot.disabled.length > 0 &&
                      ` · ${translate("ai_engine_history_disabled").replace(
                        "{count}",
                        String(entry.snapshot.disabled.length)
                      )}`}
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      startIcon={<HistoryIcon />}
                      onClick={() => restore(entry)}
                    >
                      {translate("ai_engine_rollback")}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Stack>
    </Paper>
  )
}
